import React, { useState } from 'react';
import { useAuth, useCampaignEvents } from 'api';
import { Text, Loading, Card, Button } from 'ui';
import { get } from 'lodash';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveLine } from '@nivo/line';
import moment from 'moment';
import "./index.css";
import { green, orange, pink, darkBlue, lightBlue, yellow } from 'config/colors';


export default ({campaignId}) => {
  const auth = useAuth();
  const companyId = auth && auth.user.companyId;
  const [ page, setPage ] = useState(0);
  const to = moment().subtract(page, 'months');
  const from = moment().subtract(page+1, 'months');
  const events = useCampaignEvents(companyId, campaignId, from.valueOf(), to.valueOf());
  
  const [locationFilter, setLocationFilter] = useState('country_name');
  const [deviceFilter, setDeviceFilter] = useState('');
  
  if(!events) {
    return <Loading/>;
  }
  
  // if(events.length === 0) {
  //   return <Text>No Data</Text>;
  // }
  
  
  
  // Report Data
  
  const totalViews = events.reduce((total, e) => {
    if(e.type === 'VIEW') {
      return total + 1; 
    }
    return total;
  }, 0);
  
  const totalTaps = events.reduce((total, e) => {
    if(e.type === 'VIEW' && !e.referrer) {
      return total + 1; 
    }
    return total;
  }, 0);
  
  const totalClicks = events.reduce((total, e) => {
    if(e.type === 'CLICK') {
      return total + 1; 
    }
    return total;
  }, 0);
  
  const sessions = events.reduce((res, e) => {
    let ip = get(e, 'location.IPv4', null);
    if(!ip) return res;
    if(!res[ip]) res[ip] = 0;
    res[ip] += 1;
    return res;
  }, {});
  
  const aveSessions = Math.round(
    Object.keys(sessions)
      .map(ip => sessions[ip])
      .reduce((ave, cnt) => {
        if(!ave) return cnt;
        return (ave + cnt) / 2;
      }, null)
  );
  
  const uniqueUsers = Object.keys(sessions).length;
  
  const conversionRate = totalClicks && totalViews ? Math.round(totalClicks / totalViews * 100) : 0;
  const bounceRate = 100 - conversionRate;
  
  const sources = events.reduce((res, e) => {
    if(e.type !== "VIEW") return res;
    let referrer = e.referrer || "Tapped Tag";
    if(referrer.length > 30) {
      referrer = referrer.slice(0, 27) + "...";
    }
    let src = res.find(s => s.id === referrer);
    if(!src) {
      src = {
        id: referrer,
        value: 0,
      };
      res.push(src);
    }
    src.value += 1;
    return res;
  }, []);
  
  const clicks = events.reduce((res, e) => {
    if(e.type !== "CLICK") return res;
    let href = e.href;
    if(!href) return res;
    let click = res.find(s => s.id === href);
    if(!click) {
      click = {
        id: href,
        value: 0,
      };
      res.push(click);
    }
    click.value += 1;
    return res;
  }, []);
  
  // const firstView = events
  //   .filter(e => e.type === 'VIEW')
  //   .reduce((first, e) => {
  //     if(!first || e.date < first) {
  //       first = e.date;
  //     }
  //     return first;
  //   }, null);
  
  // const lastView = events
  //   .filter(e => e.type === 'VIEW')
  //   .reduce((last, e) => {
  //     if(!last || e.date > last) {
  //       last = e.date;
  //     }
  //     return last;
  //   }, null);
  
  let views = [];
  let date = moment(from);
  while(date.isBefore(to)) {
    views.push({
      x: date.format("MM/DD"),
      y: events
          .filter(e => e.type === "VIEW")
          .filter(e => moment(e.date).format("MM/DD") === date.format("MM/DD"))
          .length
    });
    date.add(1, 'day');
  }
  
  const aveTime = Math.round(
    events
      .filter(e => e.type === 'VIEW')
      .filter(e => e.date && e.dateEnd)
      .reduce((ave, e) => {
        const duration = moment.duration(e.dateEnd - e.date).minutes();
        if(ave) {
          return (ave + duration) / 2;
        }
        return duration;
      }, null)
  );
    
  const onlineNow = events.reduce((total, i) => {
    if(i.date && !i.dateEnd && moment().diff(i.date) < 60 * 60 * 1000) {
      return total + 1;
    }
    return total;
  }, 0);
  
  const locations = events.reduce((all, e) => {
    if(e.type !== "VIEW") {
      return all;
    }
    let key = get(e, `location.${locationFilter}`, null);
    if(!all[key]) {
      all[key] = 1;
    }
    else {
      all[key] += 1;
    }
    return all;
  }, {});
  
  const platforms = events
    .filter(e => e.type === 'VIEW')
    .filter(e => {
      if(!deviceFilter) {
        return true;
      }
      const category = get(e, 'userAgent.deviceCategory', null) || get(e, 'userAgent.device.type', null);
      return category === deviceFilter;
    })
    .reduce((all, e) => {
      let os = get(e, 'userAgent.os.name', null);
      if(!os) return all;
      if(!all[os]) {
        all[os] = 0;
      }
      all[os] += 1;
      return all;
    }, {});
  
  
  // Render
  
  return (
    <div>
      <div className="report-title">
        <div>
          <Text bold block color="#00b2a8" style={{paddingTop:16}}>Campaign Performance Totals</Text> 
        </div>
        <div>
          <Button onClick={() => setPage(page+1)}  color={green}>
            <span className="fas fa-caret-left"/>
          </Button>
          <div>
            <Text block small color="#aaa">Date Range</Text>
            <Text>{from.format("MM/DD/YY")} - {to.format("MM/DD/YY")}</Text>
          </div>
          <Button onClick={() => setPage(Math.max(page-1, 0))}  color={green}>
            <span className="fas fa-caret-right"/>
          </Button>
        </div>
      </div>
      
      <div className="report-metrics">
        <div>
          <Text block small bold>Total Views</Text>
          <Text block huge color="#00b2a8">{totalViews}</Text>
        </div>
        <div>
          <Text block small bold>Total Taps</Text>
          <Text block huge color="#00b2a8">{totalTaps}</Text>
        </div>
        <div>
          <Text block small bold>Unique Users</Text>
          <Text block huge color="#00b2a8">{uniqueUsers}</Text>
        </div>
        <div>
          <Text block small bold>Total Clicks</Text>
          <Text block huge color="#00b2a8">{totalClicks}</Text>
        </div>
        <div>
          <Text block small bold>Conversions</Text>
          <Text block huge color="#00b2a8">{conversionRate}%</Text>
        </div>
      </div>
      
      
      <div className="report-charts">
        <div>
          <Text block small bold style={{marginBottom:16}}>Tap Sources</Text>
          <ResponsivePie
            data={sources}
            colors={[darkBlue, orange, green, pink, lightBlue, yellow]}
            width={400}
            height={200}
            margin={{ top:0, right:200, bottom:0, left:0 }}
            innerRadius={0}
            cornerRadius={0}
            enableRadialLabels={false}
            slicesLabelsTextColor={"#ffffff"}
            legends={[{
              anchor: 'top-right',
              direction: 'column',
              itemWidth: 200,
              itemHeight: 24,
              translateX: 216,
              itemTextColor: '#424242',
              symbolSize: 18,
              symbolShape: 'circle',
            }]}
          />
        </div>
        <div>
          <Text block small bold style={{marginBottom:16}}>Click Details</Text>
          <ResponsivePie
            data={clicks}
            colors={[darkBlue, orange, green, pink, lightBlue, yellow]}
            width={400}
            height={200}
            margin={{ top:0, right:200, bottom:0, left:0 }}
            innerRadius={0}
            cornerRadius={0}
            enableRadialLabels={false}
            slicesLabelsTextColor={"#ffffff"}
            legends={[{
              anchor: 'top-right',
              direction: 'column',
              itemWidth: 200,
              itemHeight: 24,
              translateX: 216,
              itemTextColor: '#424242',
              symbolSize: 18,
              symbolShape: 'circle',
            }]}
          />
        </div>
      </div>
      
      <div className="report-title" style={{marginBottom:16}}>
        <Text bold block color="#00b2a8">Campaign Audience</Text> 
      </div>
      
      <div className="report-audiencechart">
        <ResponsiveLine
          data={[{
            id: 'views',
            data: views,
          }]}
          margin={{ top:16, right:40, bottom:20, left:16 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', stacked: true, min: 0, max: 'auto' }}
          enableGridX={false}
          axisTop={null}
          axisLeft={null}
          axisBottom={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
          }}
          axisRight={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
          }}
          colors={[green]}
          pointSize={10}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          useMesh={true}
          enableCrosshair={false}
          tooltip={data => {
            return (
              <Card style={{padding:8}}>
                <Text block small>{data.point.data.x}</Text>
                <Text block>{data.point.data.y} <Text small>views</Text></Text>
              </Card>
            );
          }}
        />
      </div>
      
      <div className="report-audience">
        <div>
          <Text block light small>Online Now</Text>
          <Text block huge color="#00b2a8">{onlineNow}</Text>
        </div>
        <div>
          <Text block light small>Users</Text>
          <Text block huge color="#00b2a8">{totalViews}</Text>
        </div>
        <div>
          <Text block light small>Unique Users</Text>
          <Text block huge color="#00b2a8">{uniqueUsers}</Text>
        </div>
        <div>
          <Text block light small>Sessions / User</Text>
          <Text block huge color="#00b2a8">{Math.round(aveSessions)}</Text>
        </div>
        <div>
          <Text block light small>Ave. Time on Page</Text>
          <Text block huge color="#00b2a8">{aveTime} min.</Text>
        </div>
        <div>
          <Text block light small>Bounce Rate</Text>
          <Text block huge color="#00b2a8">{bounceRate}%</Text>
        </div>
      </div>
      
      <div className="report-title" style={{marginBottom:16}}>
        <Text bold block color="#00b2a8">Audience Details</Text> 
      </div>
      
      <div className="report-audiencedetails">
        
        <div>
          <div className="report-audiencedetails-title">
            <div>
              <Text>Location</Text>
            </div>
            <div>
              <Button className={locationFilter === "city" ? "active" : ""} onClick={() => setLocationFilter("city")}>City</Button>
              <Button className={locationFilter === "state" ? "active" : ""} onClick={() => setLocationFilter("state")}>State</Button>
              <Button className={locationFilter === "country_name" ? "active" : ""} onClick={() => setLocationFilter("country_name")}>Country</Button>
            </div>
          </div>
          {Object.keys(locations).map(key => 
            <div className="report-audiencedetails-row" key={key}>
              <div><Text small>{key}</Text></div>
              <div><Text>{locations[key]}</Text></div>
            </div>
          )}
        </div>
        
        <div>
          <div className="report-audiencedetails-title">
            <div>
              <Text>Device</Text>
            </div>
            <div>
              <Button className={deviceFilter === "desktop" ? "active" : ""} onClick={() => setDeviceFilter("desktop")}>Desktop</Button>
              <Button className={deviceFilter === "tablet" ? "active" : ""} onClick={() => setDeviceFilter("tablet")}>Tablet</Button>
              <Button className={deviceFilter === "mobile" ? "active" : ""} onClick={() => setDeviceFilter("mobile")}>Mobile</Button>
            </div>
          </div>
          {Object.keys(platforms).map(key => 
            <div className="report-audiencedetails-row" key={key}>
              <div><Text small>{key}</Text></div>
              <div><Text>{platforms[key]}</Text></div>
            </div>
          )}
        </div>
        
        <div>
          <div className="report-audiencedetails-title">
            <div>
              <Text>Sources</Text>
            </div>
          </div>
          {sources.map(src => 
            <div className="report-audiencedetails-row" key={src.id}>
              <div><Text small>{src.id}</Text></div>
              <div><Text>{src.value}</Text></div>
            </div>
          )}
        </div>
        
        <div>
          <div className="report-audiencedetails-title">
            <div>
              <Text>Clicks</Text>
            </div>
          </div>
          {clicks.map(click => 
            <div className="report-audiencedetails-row" key={click.id}>
              <div><Text small>{click.id}</Text></div>
              <div><Text>{click.value}</Text></div>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};